import type { ReactNode } from 'react';
import {
  BriefcaseBusiness,
  CalendarX2,
  Info,
  MapPin,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { StateMessage } from '@/components/common/StateMessage';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatLocation } from '@/utils/formatLocation';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import type { MoTravelRoute, WiderPeriodAction } from '../types/moTravel.types';
import type { DayRoute } from '../utils/dayRoutes';
import { MoActivityTimeline } from './MoActivityTimeline';
import { MoApprovalBar } from './MoApprovalBar';

const MO_ROLE_TITLE = 'Marketing Officer';
const ROUTE_DISCLAIMER = 'Approximate route from DCR locations, not live GPS.';
const TIMELINE_SKELETON_ROW_COUNT = 4;
const TIMELINE_SKELETON_ROW_KEYS = Array.from(
  { length: TIMELINE_SKELETON_ROW_COUNT },
  (_, rowNumber) => `timeline-skeleton-${rowNumber}`,
);

interface FactRowProps {
  icon: LucideIcon;
  children: ReactNode;
}

function FactRow({ icon: Icon, children }: FactRowProps) {
  return (
    <li className="flex min-w-0 items-center gap-2">
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="flex min-w-0 flex-1 items-center gap-1.5">{children}</span>
    </li>
  );
}

interface ProfileHeaderProps {
  moName: string | null;
  designation: string;
  hrId: string | null | undefined;
  areaManagerName: string | null | undefined;
  area: string | null;
}

function ProfileHeader({ moName, designation, hrId, areaManagerName, area }: ProfileHeaderProps) {
  return (
    <header className="flex flex-col gap-3 p-4">
      {moName === null ? (
        <Skeleton className="h-5 w-44" aria-hidden />
      ) : (
        <h2 className="truncate text-lg leading-tight font-semibold">{moName}</h2>
      )}
      <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
        <FactRow icon={BriefcaseBusiness}>
          <span className="truncate text-foreground">{designation}</span>
          {hrId && <span className="shrink-0 tabular-nums">({hrId})</span>}
        </FactRow>
        {areaManagerName && (
          <FactRow icon={UserRound}>
            <span
              className={cn(
                'shrink-0 rounded px-1 py-0.5 text-2xs leading-none font-semibold',
                ACCENT_MARK_CLASS,
              )}
            >
              AM
            </span>
            <span className="truncate text-foreground">{areaManagerName}</span>
          </FactRow>
        )}
        {area && (
          <FactRow icon={MapPin}>
            <span className="truncate">{area}</span>
          </FactRow>
        )}
      </ul>
    </header>
  );
}

interface NoEntriesStateProps {
  moName: string;
  periodLabel: string;
  widerPeriodAction: WiderPeriodAction | null;
}

function NoEntriesState({ moName, periodLabel, widerPeriodAction }: NoEntriesStateProps) {
  return (
    <div className="flex justify-center border-t px-4 py-6">
      <StateMessage
        icon={CalendarX2}
        tone="neutral"
        title="No DCR entries"
        description={`${moName} has no activity for ${periodLabel}.`}
        size="compact"
        actions={
          widerPeriodAction ? (
            <Button variant="outline" size="sm" onClick={widerPeriodAction.onClick}>
              {widerPeriodAction.label}
            </Button>
          ) : null
        }
      />
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {TIMELINE_SKELETON_ROW_KEYS.map((rowKey) => (
        <Skeleton key={rowKey} className="h-14" />
      ))}
    </div>
  );
}

interface MoTravelDetailPanelProps {
  route: MoTravelRoute | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  timelineRoutes: DayRoute[];
  isDayFiltered: boolean;
  statsDate: string | null;
  hoveredKey: string | null;
  focusedKey: string | null;
  onStopHover: (waypointKey: string | null) => void;
  onStopSelect: (waypointKey: string) => void;
  periodLabel: string;
  widerPeriodAction: WiderPeriodAction | null;
}

export function MoTravelDetailPanel({
  route,
  isLoading,
  error,
  onRetry,
  timelineRoutes,
  isDayFiltered,
  statsDate,
  hoveredKey,
  focusedKey,
  onStopHover,
  onStopSelect,
  periodLabel,
  widerPeriodAction,
}: MoTravelDetailPanelProps) {
  const profile = route?.profile;
  const moName = isLoading ? null : (profile?.fullName ?? null);
  const area = formatLocation({
    thanaName: profile?.thana ?? null,
    districtName: profile?.zilla ?? null,
    divisionName: profile?.division ?? null,
  });
  const hasError = Boolean(error);
  const hasNoEntries = !isLoading && !hasError && route?.summary.totalEntry === 0;
  const isProfileShown = isLoading || profile !== undefined;
  const approvalDays =
    route && statsDate ? route.days.filter((day) => day.date === statsDate) : route?.days;

  function renderTimeline() {
    if (hasNoEntries) return null;
    if (isLoading) return <TimelineSkeleton />;
    if (hasError || !route) {
      return (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the travel route"
          onRetry={onRetry}
        />
      );
    }
    return (
      <>
        <section aria-label="Activity timeline" className="flex flex-col gap-2.5">
          <h3 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
            Activity timeline
          </h3>
          <MoActivityTimeline
            routes={timelineRoutes}
            emptyMessage={
              isDayFiltered ? 'No entries on this day.' : 'No entries in the selected period.'
            }
            focusedKey={focusedKey}
            hoveredKey={hoveredKey}
            onStopHover={onStopHover}
            onStopSelect={onStopSelect}
          />
        </section>

        <p className="flex items-start gap-1.5 text-2xs text-muted-foreground">
          <Info className="mt-0.5 size-3 shrink-0" aria-hidden />
          {ROUTE_DISCLAIMER}
        </p>
      </>
    );
  }

  return (
    <div className="lg:h-full lg:overflow-y-auto">
      <div className="flex flex-col gap-5 p-4">
        {isProfileShown && (
          <section
            aria-label="Marketing Officer summary"
            className="overflow-hidden rounded-xl border bg-card shadow-xs"
          >
            <ProfileHeader
              moName={moName}
              designation={profile?.designation ?? MO_ROLE_TITLE}
              hrId={profile?.hrId}
              areaManagerName={profile?.amName}
              area={area}
            />
            {approvalDays && <MoApprovalBar days={approvalDays} className="border-t p-4" />}
            {hasNoEntries && moName && (
              <NoEntriesState
                moName={moName}
                periodLabel={periodLabel}
                widerPeriodAction={widerPeriodAction}
              />
            )}
          </section>
        )}

        {renderTimeline()}
      </div>
    </div>
  );
}
